import { useState } from 'react';
import { Sheet } from '../components/Sheet.jsx';
import { Icon } from '../components/Icon.jsx';
import { useAbwesenheitBeantragen, useAbwesenheitsArten } from '../hooks/data.js';
import { addDays, isoDate } from '../lib/format.js';
import { montag } from '../lib/kopieren.js';
import { newKey } from '../lib/outbox.js';
import { load, save } from '../lib/storage.js';
import { useToast } from '../state/toast.jsx';

const KURZ = new Intl.DateTimeFormat('de-CH', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });
const datumText = (iso) => KURZ.format(new Date(`${iso}T12:00:00`));
/** Arbeitstage Mo–Fr zwischen zwei Daten (inkl.), ohne Feiertage. */
function arbeitstage(von, bis) {
  let n = 0;
  for (let d = von, i = 0; d <= bis && i < 400; d = addDays(d, 1), i++) { const t = new Date(`${d}T12:00:00`).getDay(); if (t !== 0 && t !== 6) n++; }
  return n;
}

/** Ferien oder andere Abwesenheit beantragen (ganze Tage). Genehmigt wird im Büro in Exxas. */
export function AbwesenheitSheet({ onClose }) {
  const arten = useAbwesenheitsArten();
  const beantragen = useAbwesenheitBeantragen();
  const toast = useToast();
  const heute = isoDate();
  const naechsterMontag = addDays(montag(heute), 7);
  const [art, setArt] = useState(null);
  const [von, setVon] = useState(naechsterMontag);
  const [bis, setBis] = useState(addDays(naechsterMontag, 4));
  const [kommentar, setKommentar] = useState('');
  const [fehler, setFehler] = useState('');
  const [key, setKey] = useState(newKey);
  const [beantragt, setBeantragt] = useState(() => load('abwesenheiten', []));
  const gewaehlt = art ?? arten.data?.[0]?.id ?? null;
  const tage = von && bis && bis >= von ? arbeitstage(von, bis) : 0;

  const schnell = [
    ['Morgen', addDays(heute, 1), addDays(heute, 1)],
    ['Nächste Woche', naechsterMontag, addDays(naechsterMontag, 4)],
    ['In 2 Wochen', addDays(naechsterMontag, 7), addDays(naechsterMontag, 11)],
  ];

  const senden = async () => {
    setFehler('');
    if (!gewaehlt) { setFehler('Bitte die Art wählen.'); return; }
    if (!von || !bis || bis < von) { setFehler('«Bis» darf nicht vor «Von» liegen.'); return; }
    try {
      const r = await beantragen.mutateAsync({ kategorie: gewaehlt, von, bis, kommentar: kommentar.trim(), key });
      const titel = arten.data?.find((a) => a.id === gewaehlt)?.titel ?? 'Abwesenheit';
      const liste = [{ titel, von, bis, am: r.beantragtAm }, ...beantragt].slice(0, 10);
      save('abwesenheiten', liste);
      setBeantragt(liste);
      setKey(newKey());
      setKommentar('');
      toast({ text: `${titel} ${datumText(von)} bis ${datumText(bis)} beantragt` });
    } catch (err) { setFehler(err.message); }
  };

  return (
    <Sheet title="Abwesenheit beantragen" onClose={onClose}>
      <div className="stack" style={{ gap: 8 }}>
        <span className="label">Art</span>
        {arten.isLoading && <div className="skeleton" style={{ height: 48 }} />}
        <div className="wrapchips" role="group" aria-label="Art der Abwesenheit">
          {(arten.data ?? []).map((a) => (
            <button key={a.id} type="button" className={`chip${gewaehlt === a.id ? ' on' : ''}`} aria-pressed={gewaehlt === a.id} onClick={() => setArt(a.id)}>{a.titel}</button>
          ))}
        </div>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <span className="label">Zeitraum</span>
        <div className="wrapchips">
          {schnell.map(([label, a, b]) => (
            <button key={label} type="button" className={`chip${von === a && bis === b ? ' on' : ''}`} onClick={() => { setVon(a); setBis(b); }}>{label}</button>
          ))}
        </div>
        <div className="two" style={{ gap: 10 }}>
          <label className="field"><span className="label">Von</span><input className="input" type="date" value={von} onChange={(e) => { setVon(e.target.value); if (e.target.value > bis) setBis(e.target.value); }} /></label>
          <label className="field"><span className="label">Bis</span><input className="input" type="date" value={bis} min={von} onChange={(e) => setBis(e.target.value)} /></label>
        </div>
        {tage > 0 && <span className="sub">{tage} {tage === 1 ? 'Arbeitstag' : 'Arbeitstage'} (Mo bis Fr, ohne Feiertage), ganze Tage</span>}
      </div>

      <label className="field">
        <span className="label">Bemerkung ans Büro (optional)</span>
        <textarea className="input" rows={2} maxLength={500} value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
      </label>

      {fehler && <p className="error" role="alert">{fehler}</p>}
      <button className="btn lg block" onClick={senden} disabled={beantragen.isPending || !gewaehlt || !tage}>
        {beantragen.isPending ? 'Wird beantragt …' : 'Beantragen'}
      </button>
      <span className="sub">Der Antrag geht in Exxas an das Büro und wird dort genehmigt.</span>

      {beantragt.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="label">Von diesem Gerät beantragt</span>
          {beantragt.map((b, i) => (
            <div key={i} className="row" style={{ gap: 10 }}>
              <span className="pill info"><Icon name="check" size={12} stroke={2.8} />Beantragt</span>
              <span style={{ fontSize: 14 }}>{b.titel}: {datumText(b.von)}{b.bis !== b.von ? ` bis ${datumText(b.bis)}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
